import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid2,
  IconButton,
  Link,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomTypography from "../../../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../../../views/components/common/SkuCopyRow";
import CustomTextField from "../../../../../../../views/components/common/textFields/TextField";
import AppSearchDropdownMulti from "../../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import AppTablePagenation from "../../../../../../../views/components/common/pages/AppTablePagenation";
import InlineFormError from "../../../../../../../views/components/common/form/InlineFormError";
import { useDebounce } from "../../../../../../../views/components/common/hooks/Debounce";
import { formatDate } from "../../../../../../../views/components/common/functions/formatDate";
import {
  getModernPageColors,
  modernDrawerBodySx,
  modernDrawerHeaderSx,
  modernSectionPaperSx,
} from "../../../../../../../styles/modernListingPageSx";
import {
  INVENTORY_ADJUSTMENT_TYPE_OPTIONS,
  InventoryAdjustmentType,
  InventoryAuditLogRow,
} from "../../../../../../../models/SwageazyInventory/SwageazyInventoryAdjustment";
import SwageazyInventoryAdjustmentStore from "../../../../../stores/SwageazyInventoryAdjustmentStore";

interface SwageazyInventoryAuditLogDrawerProps {
  store: SwageazyInventoryAdjustmentStore;
}

interface DetailCellProps {
  label: string;
  value?: React.ReactNode;
  /** When set, renders the SKU with a copy button instead of `value`. */
  sku?: string | null;
  skuSuffix?: string;
}

const DetailCell: React.FC<DetailCellProps> = ({
  label,
  value,
  sku,
  skuSuffix,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  return (
    <Grid2 size={{ xs: 6, sm: 4 }}>
      <CustomTypography variant="body3" color={colors.subtitle}>
        {label}
      </CustomTypography>
      {sku !== undefined ? (
        <SkuCopyRow
          sku={sku}
          suffix={skuSuffix}
          emptyFallback="—"
          fontWeight={600}
          color={colors.title}
        />
      ) : (
        <CustomTypography
          variant="body2"
          fontWeight={600}
          color={colors.title}
          sx={{ wordBreak: "break-word" }}
        >
          {value ?? "—"}
        </CustomTypography>
      )}
    </Grid2>
  );
};

const AuditLogCard: React.FC<{ row: InventoryAuditLogRow }> = ({ row }) => {
  const theme = useTheme();
  const c = theme.palette.customColors;
  const colors = getModernPageColors(theme);
  const isTransfer = row.adjustment_type === "sku_transfer";
  const qty = Number(row.quantity) || 0;

  const pillSx = { height: 22, fontSize: "0.7rem", fontWeight: 700 };

  return (
    <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              label={isTransfer ? "SKU Transfer" : "Adjustment"}
              sx={{
                ...pillSx,
                color: isTransfer ? c.purpleMain : c.darkBlue,
                bgcolor: isTransfer ? c.lightPurple : c.blueLightBackground,
              }}
            />
            <Chip
              size="small"
              variant="outlined"
              label={row.source === "csv" ? "CSV" : "Manual"}
              sx={pillSx}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`#${row.adjustment_id}`}
              sx={pillSx}
            />
          </Box>
          <CustomTypography
            variant="body3"
            color={colors.subtitle}
            sx={{ mt: 1 }}
          >
            {formatDate(row.created_date)}
            {row.admin_name ? ` · ${row.admin_name}` : ""}
            {row.platform ? ` · ${row.platform}` : ""}
          </CustomTypography>
        </Box>
        <Box textAlign={{ xs: "left", sm: "right" }}>
          <CustomTypography
            variant="heading5"
            color={qty < 0 ? colors.debit : colors.credit}
          >
            {qty > 0 ? "+" : ""}
            {qty}
          </CustomTypography>
          <CustomTypography variant="body3" color={colors.subtitle}>
            {row.quantity_before} → {row.quantity_after}
          </CustomTypography>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Grid2 container spacing={1.5}>
        <DetailCell label="SKU" sku={row.sku} />
        <DetailCell label="Warehouse" value={row.warehouse_id} />
        <DetailCell label="Box" value={row.box_id} />
        {isTransfer && (
          <>
            <DetailCell
              label="From"
              sku={row.from_sku}
              skuSuffix={` (#${row.from_product_id ?? "—"})`}
            />
            <DetailCell
              label="To"
              sku={row.to_sku}
              skuSuffix={` (#${row.to_product_id ?? "—"})`}
            />
            <DetailCell
              label="Product"
              value={row.product_name || `#${row.product_id}`}
            />
          </>
        )}
      </Grid2>

      <Box sx={{ mt: 1.5 }}>
        <CustomTypography variant="body3" color={colors.subtitle}>
          Reason
        </CustomTypography>
        <CustomTypography
          variant="body2"
          color={colors.title}
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {row.reason || "—"}
        </CustomTypography>
        {row.note && (
          <CustomTypography
            variant="body3"
            color={colors.subtitle}
            sx={{ mt: 0.5, wordBreak: "break-word" }}
          >
            Note: {row.note}
          </CustomTypography>
        )}
        {row.sheet_url && (
          <Link
            href={row.sheet_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-block",
              mt: 0.5,
              color: colors.accent,
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            View uploaded sheet
          </Link>
        )}
      </Box>
    </Paper>
  );
};

const SwageazyInventoryAuditLogDrawer: React.FC<
  SwageazyInventoryAuditLogDrawerProps
> = ({ store }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [skuSearch, setSkuSearch] = useState("");
  const debouncedSku = useDebounce(skuSearch, 400);

  useEffect(() => {
    if (!store.isAuditOpen) {
      setSkuSearch("");
      return;
    }
    if ((store.auditFilters.sku || "") === debouncedSku.trim()) return;
    store.setAuditFilters({ sku: debouncedSku.trim() || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSku, store.isAuditOpen]);

  const rows = store.auditRows;
  const added = rows
    .filter((row) => row.quantity > 0)
    .reduce((sum, row) => sum + Number(row.quantity), 0);
  const removed = rows
    .filter((row) => row.quantity < 0)
    .reduce((sum, row) => sum + Math.abs(Number(row.quantity)), 0);

  const typeValue = INVENTORY_ADJUSTMENT_TYPE_OPTIONS.filter(
    (option) => option.id === (store.auditFilters.type || ""),
  );

  return (
    <Drawer
      anchor="right"
      open={store.isAuditOpen}
      onClose={store.closeAudit}
      sx={{
        zIndex: 2100,
        "& .MuiDrawer-paper": { zIndex: 2100 },
      }}
    >
      <Box
        sx={{
          width: { xs: "100vw", sm: 560, md: 640 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: theme.palette.mode === "dark" ? "#0F172A" : colors.pageBg,
        }}
      >
        <Box sx={modernDrawerHeaderSx(theme)}>
          <Box sx={{ minWidth: 0 }}>
            <CustomTypography variant="heading4" color={colors.title}>
              Adjustment Audit Logs
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={colors.subtitle}
              sx={{ mt: 0.5, wordBreak: "break-word" }}
            >
              {store.auditTitle || "—"}
              {store.auditFilters.product_id
                ? ` · #${store.auditFilters.product_id}`
                : ""}
            </CustomTypography>
          </Box>
          <IconButton onClick={store.closeAudit}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Grid2 container spacing={1.5} sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 2 }}>
          <Grid2 size={{ xs: 12, sm: 7 }}>
            <CustomTextField
              placeholder="Filter by SKU"
              value={skuSearch}
              onChange={(e) => setSkuSearch(e.target.value)}
              width="100%"
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 5 }}>
            <AppSearchDropdownMulti
              title="Type"
              isMultiSelect={false}
              local
              list={INVENTORY_ADJUSTMENT_TYPE_OPTIONS}
              value={typeValue}
              handleSelect={(selected) =>
                store.setAuditFilters({
                  type: (selected?.[0]?.id || "") as
                    | InventoryAdjustmentType
                    | "",
                })
              }
            />
          </Grid2>
          {[
            { label: "Entries", value: store.auditTotalCount },
            {
              label: isPhone ? "Added (page)" : "Added (this page)",
              value: `+${added}`,
            },
            {
              label: isPhone ? "Removed (page)" : "Removed (this page)",
              value: `−${removed}`,
            },
          ].map((tile) => (
            <Grid2 key={tile.label} size={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.customColors.softCardBorder}`,
                  bgcolor: theme.palette.customColors.softCardBackground,
                }}
              >
                <CustomTypography
                  variant="body3"
                  color={theme.palette.customColors.mutedSlateText}
                >
                  {tile.label}
                </CustomTypography>
                <CustomTypography
                  variant="heading5"
                  color={theme.palette.customColors.navyText}
                >
                  {tile.value}
                </CustomTypography>
              </Paper>
            </Grid2>
          ))}
        </Grid2>

        <Box sx={modernDrawerBodySx()}>
          <InlineFormError message={store.auditError} sx={{ mt: 0 }} />
          {store.isAuditLoading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            !store.auditError && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  textAlign: "center",
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <CustomTypography variant="body2" color={colors.subtitle}>
                  No adjustments or SKU transfers found.
                </CustomTypography>
              </Paper>
            )
          ) : (
            rows.map((row) => <AuditLogCard key={row.id} row={row} />)
          )}
        </Box>

        {store.auditTotalCount > 0 && (
          <Box
            sx={{
              borderTop: `1px solid ${colors.border}`,
              bgcolor: colors.surface,
            }}
          >
            <AppTablePagenation
              fontSize="12px"
              pageNo={store.auditFilters.page_no}
              limit={store.auditFilters.limit}
              totalCount={store.auditTotalCount}
              rowsPerPageOptions={[20, 50, 100]}
              showLabelRowsPerPage={!isPhone}
              onPageChange={(_event, page) =>
                store.setAuditFilters({ page_no: page })
              }
              handleRowsChange={(limit) =>
                store.setAuditFilters({ limit, page_no: 1 })
              }
            />
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default observer(SwageazyInventoryAuditLogDrawer);
